import { ApiService, type ApiRequestConfig } from "./api.service";
import type { TProjectLmsResults } from "@/types/lms";

/** The project results endpoints (plan §6.6, NG-775, backend NG-675).
 *
 *  **`POST` recomputes, it does not fetch.** Both verbs hit the same route and both
 *  answer the same body — one entry per active link — because the caller pressed refresh
 *  to see numbers, and a second round trip for what the server just computed is a spinner
 *  nobody needed. Nothing here calls the vendor: the recompute reads attempt rows Cognota
 *  already holds, so the copy around this must never promise new LMS data.
 *
 *  Budgets, per `/response-time-limits`: the `GET` is a read that gates the next action,
 *  so p99 < 1s, and it is five queries whatever the learner count (the backend's
 *  scale-invariance guard pins that). The `POST` is a bounded write — one rebuild per
 *  link, a handful at most — inside the p99 < 2.5s write budget, but it is user-initiated
 *  and watched, which puts it in the 1–2s band where feedback is mandatory. Its caller
 *  owes a spinner and blocks the button, which is why this one write is not optimistic:
 *  the new numbers are computed server-side and are genuinely unknowable on the client.
 */
export class LmsResultsService extends ApiService {
  async listResults(
    slug: string,
    projectId: string,
    config?: ApiRequestConfig,
  ): Promise<TProjectLmsResults[]> {
    return this.get(`/api/workspaces/${slug}/projects/${projectId}/lms-results/`, config);
  }

  /** Recompute this project's rollups from stored attempts and hand back the new numbers.
   *
   *  `project.edit`, unlike the `GET` beside it: cheap and idempotent is not the same as
   *  read-only, and this replaces stored rows. The caller hides the control accordingly
   *  rather than letting the API refuse a button somebody could see.
   */
  async refreshResults(slug: string, projectId: string): Promise<TProjectLmsResults[]> {
    // No body: the endpoint declares `request=None` and recomputes every active link on
    // the project, so there is nothing for a caller to parameterise.
    return this.post(`/api/workspaces/${slug}/projects/${projectId}/lms-results/`);
  }
}
